// The Change queue tab's rows (BL-2438, ADR 066 — Offline Mode § 4.6): queued entries grouped per
// lot under the label each captured when it was queued (§ 8), a lot's edit run as one row, its
// photos, videos and documents as one row each, and which actions each row offers. Pure:
// `deriveQueueStatus` (BL-2685) reads this mapping for the page, the offline bar and the sidebar.
import { formatMoneyText, type MoneyWire } from '@auctogon/core/money';
import { lotGroupKey, readQueuedEntry, type ChangeQueueJson, type ChangeQueueLabel } from './change-queue-entry';
import type { LotConflictField, LotDesiredState } from './lot-desired-state';
import { replayFailureReasonText, type ReplayFailureInput } from './failure-reason';
import { mediaOf } from './lot-state-media';
import { dependentsOf, type QueueEntryView } from './change-queue-plan';

/** What the mapping reads of a stored entry (the store's `OutboxEntry` satisfies it). */
export interface ChangeQueueDisplayEntry extends QueueEntryView {
  createdAt: string;
  error: ReplayFailureInput | null;
}

/** What a row is, which decides its title. */
export type ChangeQueueRowKind =
  | 'create'
  | 'edit'
  | 'move'
  | 'status'
  | 'relist'
  | 'photos'
  | 'videos'
  | 'media-remove'
  | 'media-order'
  | 'documents'
  | 'document-remove'
  | 'note-create'
  | 'note-update'
  | 'note-delete'
  | 'unreadable';

/**
 * A row's status word: "Not synced", "Syncing", "Failed", or "Conflict" for a change the server
 * refused because a field it edits has a newer value there (BL-2569, operator 2026-10-14).
 */
export type ChangeQueueRowStatus = 'not-synced' | 'syncing' | 'failed' | 'conflict';

/** What a row offers. Nothing is offered before a change fails. */
export type ChangeQueueRowAction = 'retry' | 'discard' | 'overwrite';

/** One row of the tab. */
export interface ChangeQueueRowView {
  /** Stable while the row's entries are queued: `edit:<first id>`, `bucket:<group>:<kind>`, `item:<id>`. */
  key: string;
  kind: ChangeQueueRowKind;
  /** The entries the row stands for now (a bucket's failed ones included). */
  count: number;
  title: string;
  entryIds: string[];
  /** The entry Retry, Discard and Overwrite name (the engine widens an edit run itself). */
  actionEntryId: string;
  status: ChangeQueueRowStatus;
  /** The recorded reason, on a row that failed by itself; null otherwise. */
  reason: string | null;
  actions: ChangeQueueRowAction[];
  firstAt: string;
  lastAt: string;
  /** A bucket's failed entries, each its own sub-row with its own actions. */
  failedItems: ChangeQueueRowView[];
  /** A lot in conflict (BL-2681): each field the phone and the server disagree on; null otherwise. */
  conflict: LotConflictField[] | null;
}

/** One lot's rows, under its heading. */
export interface ChangeQueueGroupView {
  key: string;
  /** "Lot 7 · <title>", "New lot · <title>" or "<title>"; null when no entry could be read. */
  heading: string | null;
  rows: ChangeQueueRowView[];
}

const SINGLE_KIND: Record<string, ChangeQueueRowKind> = {
  'lot.move': 'move',
  'lot.status': 'status',
  'lot.relist': 'relist',
  'note.create': 'note-create',
  'note.update': 'note-update',
  'note.delete': 'note-delete',
};

const FIXED_TITLE: Partial<Record<ChangeQueueRowKind, string>> = {
  create: 'Create lot',
  move: 'Move lot',
  status: 'Change status',
  relist: 'Relist lot',
  'media-order': 'Reorder photos',
  'note-create': 'Add note',
  'note-update': 'Edit note',
  'note-delete': 'Delete note',
  unreadable: 'Unreadable change',
};

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;

/**
 * A row's title. A bucket's count can be its peak this session, which the tab supplies.
 *
 * @param input.kind - the row's kind.
 * @param input.count - how many entries it stands for.
 * @returns the title, e.g. "Add 52 photos" or "Edit lot · 5 edits".
 */
export function changeQueueRowTitle(input: { kind: ChangeQueueRowKind; count: number }): string {
  const { kind, count } = input;
  switch (kind) {
    case 'edit':
      return count === 1 ? 'Edit lot' : `Edit lot · ${count} edits`;
    case 'photos':
      return `Add ${plural(count, 'photo', 'photos')}`;
    case 'videos':
      return `Add ${plural(count, 'video', 'videos')}`;
    case 'media-remove':
      return `Remove ${plural(count, 'photo or video', 'photos or videos')}`;
    case 'documents':
      return `Add ${plural(count, 'document', 'documents')}`;
    case 'document-remove':
      return `Remove ${plural(count, 'document', 'documents')}`;
    default:
      return FIXED_TITLE[kind] ?? 'Unreadable change';
  }
}

/**
 * @param label - the label an entry captured when it was queued.
 * @returns the group heading.
 */
export function changeQueueHeading(label: ChangeQueueLabel): string {
  if (label.newLot) return `New lot · ${label.title}`;
  if (label.lotNumber != null) return `Lot ${label.lotNumber} · ${label.title}`;
  return label.title;
}

/** The bucket a document entry joins, or null for an entry that is its own row. */
function bucketKind(entry: ChangeQueueDisplayEntry): ChangeQueueRowKind | null {
  switch (entry.op) {
    case 'lot.document.add':
      return 'documents';
    case 'lot.document.remove':
      return 'document-remove';
    default:
      return null;
  }
}

const isFailed = (e: ChangeQueueDisplayEntry) => e.status === 'failed' || e.status === 'refused';

function actionsOf(entry: ChangeQueueDisplayEntry): ChangeQueueRowAction[] {
  if (entry.status === 'failed') return ['retry', 'discard'];
  if (entry.status === 'refused') return ['discard'];
  return [];
}

function statusOf(entries: readonly ChangeQueueDisplayEntry[]): ChangeQueueRowStatus {
  if (entries.some((e) => e.status === 'syncing')) return 'syncing';
  if (entries.some((e) => e.status === 'pending')) return 'not-synced';
  return entries.some((e) => e.status === 'refused') ? 'conflict' : 'failed';
}

/** A row over entries that share one status (one entry, or one edit run). */
function plainRow(input: { key: string; kind: ChangeQueueRowKind; entries: ChangeQueueDisplayEntry[] }): ChangeQueueRowView {
  const { key, kind, entries } = input;
  const [head] = entries;
  // BUG-2066: entries are in seq order, but their times are the device clock's, which can move
  // back between two Saves. The span is the earliest to the latest, whatever order they came in.
  const times = entries.map((e) => e.createdAt).sort((a, b) => Date.parse(a) - Date.parse(b));
  return {
    key,
    kind,
    count: entries.length,
    title: changeQueueRowTitle({ kind, count: entries.length }),
    entryIds: entries.map((e) => e.id),
    actionEntryId: head.id,
    status: statusOf(entries),
    reason: isFailed(head) ? (head.error == null ? null : replayFailureReasonText(head.error)) : null,
    actions: actionsOf(head),
    firstAt: times[0],
    lastAt: times[times.length - 1],
    failedItems: [],
    conflict: null,
  };
}

/** A bucket: one row; when it holds more than one entry, its failed ones are sub-rows. */
function bucketRow(input: { groupKey: string; kind: ChangeQueueRowKind; entries: ChangeQueueDisplayEntry[] }): ChangeQueueRowView {
  const { groupKey, kind, entries } = input;
  if (entries.length === 1) return plainRow({ key: `bucket:${groupKey}:${kind}`, kind, entries });
  const failed = entries.filter(isFailed);
  const rest = entries.filter((e) => !isFailed(e));
  return {
    ...plainRow({ key: `bucket:${groupKey}:${kind}`, kind, entries }),
    status: statusOf(rest.length > 0 ? rest : entries),
    reason: null,
    actions: [],
    actionEntryId: entries[0].id,
    failedItems: failed.map((e) => plainRow({ key: `item:${e.id}`, kind, entries: [e] })),
  };
}

function headingOf(entries: readonly ChangeQueueDisplayEntry[]): string | null {
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    const read = readQueuedEntry(entries[i]);
    if (read.ok) return changeQueueHeading(read.payload.label);
  }
  return null;
}

/** One lot's rows, in the order their first entries were queued. */
function rowsOf(groupKey: string, entries: ChangeQueueDisplayEntry[]): ChangeQueueRowView[] {
  const rows: { seq: number; row: ChangeQueueRowView }[] = [];
  const buckets = new Map<ChangeQueueRowKind, ChangeQueueDisplayEntry[]>();
  for (const e of entries) {
    if (!readQueuedEntry(e).ok) {
      rows.push({ seq: e.seq, row: plainRow({ key: `item:${e.id}`, kind: 'unreadable', entries: [e] }) });
      continue;
    }
    const bucket = bucketKind(e);
    if (bucket != null) {
      if (!buckets.has(bucket)) buckets.set(bucket, []);
      buckets.get(bucket)?.push(e);
      continue;
    }
    rows.push({ seq: e.seq, row: plainRow({ key: `item:${e.id}`, kind: SINGLE_KIND[e.op] ?? 'unreadable', entries: [e] }) });
  }
  for (const [kind, bucket] of buckets) rows.push({ seq: bucket[0].seq, row: bucketRow({ groupKey, kind, entries: bucket }) });
  return rows.sort((a, b) => a.seq - b.seq).map((r) => r.row);
}

const STATE_STATUS: Record<LotDesiredState['status'], ChangeQueueRowStatus> = {
  pending: 'not-synced',
  syncing: 'syncing',
  failed: 'failed',
  refused: 'conflict',
};

/** The reason a lot's change shows while the relist that makes its copy is failed or refused. */
const HELD_BY_RELIST_REASON = 'Waiting on a relist that did not sync. Discarding that relist discards this change.';

function stateReason(state: LotDesiredState): string | null {
  if (state.error == null) return null;
  return state.status === 'failed' ? replayFailureReasonText(state.error) : state.error.message;
}

/** A lot's desired state as one row (BL-2681): "Create lot" for a lot made offline, else "Edit lot". */
function stateRow(state: LotDesiredState, heldByRelist: boolean): ChangeQueueRowView {
  const kind: ChangeQueueRowKind = state.kind === 'create' ? 'create' : 'edit';
  const failedOrRefused = state.status === 'failed' || state.status === 'refused';
  return {
    key: `state:${state.lotId}`,
    kind,
    count: 1,
    title: changeQueueRowTitle({ kind, count: 1 }),
    entryIds: [state.lotId],
    actionEntryId: state.lotId,
    status: STATE_STATUS[state.status],
    // A conflict keeps the server's "N fields changed" line (the Resolve dialog names the fields); a failure reads in plain words.
    reason: failedOrRefused ? stateReason(state) : heldByRelist ? HELD_BY_RELIST_REASON : null,
    actions: state.status === 'failed' ? ['retry', 'discard'] : state.status === 'refused' ? ['discard', 'overwrite'] : [],
    // BUG-2066: the device clock can move back between Saves, so the span is the earlier to the later of the two.
    firstAt: new Date(Math.min(state.createdAt, state.updatedAt)).toISOString(),
    lastAt: new Date(Math.max(state.createdAt, state.updatedAt)).toISOString(),
    failedItems: [],
    conflict: state.status === 'refused' ? state.conflict : null,
  };
}

/**
 * The rows for a lot's photos and videos still owed (BL-2815): how many wait to go up, how many are
 * to be removed and that the order changed. They mirror the lot's own status and offer no action of
 * their own, so Retry and Discard stay on the lot's main row.
 */
function mediaRows(state: LotDesiredState): ChangeQueueRowView[] {
  const media = mediaOf(state);
  // A photo handed to the native queue is a native row, which the native summary counts (BL-2683, BL-2685).
  const owed = media.adds.filter((a) => !a.native);
  const photos = owed.filter((a) => a.type === 'photo').length;
  const videos = owed.length - photos;
  const parts: { kind: ChangeQueueRowKind; count: number }[] = [
    { kind: 'photos', count: photos },
    { kind: 'videos', count: videos },
    { kind: 'media-remove', count: media.removed.length },
    { kind: 'media-order', count: media.order != null ? 1 : 0 },
  ];
  const main = stateRow(state, false);
  return parts
    .filter((p) => p.count > 0)
    .map((p) => ({
      ...main,
      key: `state:${state.lotId}:${p.kind}`,
      kind: p.kind,
      count: p.count,
      title: changeQueueRowTitle({ kind: p.kind, count: p.count }),
      reason: null,
      actions: [],
      conflict: null,
    }));
}

/**
 * Group the queue for the Change queue tab.
 *
 * @param entries - the queue, any order.
 * @param lotStates - the lots' desired states (BL-2681); each is one row, first in its lot's group.
 * @returns one group per lot (an entry with no lot is its own), in the order each was first queued.
 */
export function buildChangeQueueGroups(
  entries: readonly ChangeQueueDisplayEntry[],
  lotStates: readonly LotDesiredState[] = [],
): ChangeQueueGroupView[] {
  const groups = new Map<string, ChangeQueueDisplayEntry[]>();
  for (const e of [...entries].sort((a, b) => a.seq - b.seq)) {
    const key = e.groupKey ?? `entry:${e.id}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)?.push(e);
  }
  const stateByGroup = new Map(lotStates.map((state) => [lotGroupKey(state.lotId), state]));
  for (const key of stateByGroup.keys()) if (!groups.has(key)) groups.set(key, []);
  const stuckRelistIds = new Set(entries.filter((e) => e.status === 'failed' || e.status === 'refused').map((e) => e.id));
  return [...groups].map(([key, members]) => {
    const state = stateByGroup.get(key);
    const rows = rowsOf(key, members);
    const held = state != null && stuckRelistIds.has(state.lotId);
    return {
      key,
      heading: state ? changeQueueHeading(state.label) : headingOf(members),
      rows: state ? [stateRow(state, held), ...mediaRows(state), ...rows] : rows,
    };
  });
}

/**
 * What the server holds for a conflicted field, in words: "Couldn't be read" apart from "Empty".
 *
 * @param field - a Conflict row's field.
 * @returns "Couldn't be read" when the server value was never read, else the value's text.
 */
export function theirsValueText(field: { theirs: ChangeQueueJson; theirsUnread?: boolean }): string {
  return field.theirsUnread === true ? "Couldn't be read" : conflictValueText(field.theirs);
}

/**
 * A value in a Conflict row, as the operator reads it.
 *
 * @param value - a field's value from the phone's change or the server.
 * @returns text: "Empty" for nothing, money in its currency, such as "$15,000.00", anything structured as JSON.
 */
export function conflictValueText(value: ChangeQueueJson): string {
  if (value == null || value === '') return 'Empty';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (!Array.isArray(value) && typeof value.amount === 'string' && typeof value.currency === 'string') {
    return formatMoneyText(value as unknown as MoneyWire, value.currency);
  }
  return JSON.stringify(value);
}

/** What one Discard removes. */
export interface ChangeQueueDiscardScope {
  /** Every entry the engine's Discard removes: the row's own entries and what waits on them. */
  entryIds: string[];
  /** The waiting entries, named as rows, for the confirm; empty when nothing waits. */
  dependentTitles: string[];
}

/**
 * What a Discard of `id` removes, mirroring the engine's own Discard (its edit run, then
 * everything waiting on it), so the confirm names the dependents (operator Q5, 2026-10-10) and the
 * tab never mistakes a discarded row for a synced one.
 *
 * @param entries - the queue.
 * @param id - the entry being discarded.
 * @returns the scope; empty when `id` is not queued.
 */
export function discardScope(
  entries: readonly ChangeQueueDisplayEntry[],
  id: string,
  lotStates: readonly LotDesiredState[] = [],
): ChangeQueueDiscardScope {
  // A relist's copy shares its id with the relist entry, and the entry is what the operator discards.
  const state = entries.some((e) => e.id === id) ? undefined : lotStates.find((s) => s.lotId === id);
  if (state) {
    // BL-2681: Keep drops a lot's change. A lot made offline takes what waits on it, the photos and documents.
    const waiting = state.kind === 'create' ? entries.filter((e) => e.groupKey === lotGroupKey(id)) : [];
    return {
      // The state itself goes too, so the tab never reads its row as one that synced.
      entryIds: [id, ...waiting.map((e) => e.id)],
      dependentTitles: buildChangeQueueGroups(waiting).flatMap((g) => g.rows.map((r) => r.title)),
    };
  }
  const entry = entries.find((e) => e.id === id);
  if (!entry) return { entryIds: [], dependentTitles: [] };
  const doomed = new Map<string, ChangeQueueDisplayEntry>();
  for (const d of dependentsOf(entries, entry.id)) doomed.set(d.id, d);
  // A change made to this relist's copy lot waits on it, and goes with it.
  const copyState = lotStates.find((s) => s.lotId === entry.id);
  return {
    entryIds: [entry.id, ...doomed.keys(), ...(copyState ? [copyState.lotId] : [])],
    dependentTitles: [
      ...buildChangeQueueGroups([...doomed.values()]).flatMap((g) => g.rows.map((r) => r.title)),
      ...(copyState ? [changeQueueRowTitle({ kind: copyState.kind === 'create' ? 'create' : 'edit', count: 1 })] : []),
    ],
  };
}
